import React, { useState, useEffect, useRef } from 'react';
import { roomTypesApi, guestsApi, reservationsApi, proofsApi, roomsApi } from '../../services/api';
import { calcBill, fmt, PAYMENT_TYPES, ID_TYPES, NATIONALITIES, BILLING_TYPES, getTodayStr, validateImageFile, formatPhone, formatAadhaar, formatPAN } from '../../utils/billing';
import { useNotify } from '../../context/ConfirmContext';

export default function ReservationBookingPage({ hotel, onSuccess }) {
    const notify = useNotify();
    const [, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [phoneSearch, setPhoneSearch] = useState('');
    const [existingGuest, setExistingGuest] = useState(null);
    const [proofFiles, setProofFiles] = useState([]);
    const fileRef = useRef();

    const [guest, setGuest] = useState({
        name: '', phone: '', email: '', nationality: 'Indian',
        customerType: 'individual',
        idType: 'Aadhaar Card', idNumber: '',
        address: '', company: '', companyGst: '',
    });

    const [roomTypes, setRoomTypes] = useState([]);
    const [availability, setAvailability] = useState({});
    const [stay, setStay] = useState({
        checkIn: '',
        checkOut: '', checkInTime: '12:00 PM - 02:00 PM',
        nights: 1, extraCharges: '0',
        advancePaid: '0', notes: '', extension: false, extensionDate: '',
        billingCycle: '24h',
        roomConfigs: [],
        billingType: 'gst'
    });

    const getEffectiveCheckoutDate = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return checkOut;
        if (checkIn !== checkOut) return checkOut;
        const d = new Date(`${checkOut}T00:00:00.000Z`);
        d.setUTCDate(d.getUTCDate() + 1);
        return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        if (hotel && stay.checkIn && stay.checkOut) {
            reservationsApi.checkAvailabilityCounts(hotel.id, stay.checkIn, stay.checkOut)
                .then(counts => setAvailability(counts))
                .catch(err => console.error('Availability fetch error', err));
        }
    }, [hotel, stay.checkIn, stay.checkOut]);

    useEffect(() => {
        if (hotel) {
            roomTypesApi.list(hotel.id).then(types => {
                setRoomTypes(types);
                if (types.length > 0) {
                    setStay(s => s.roomConfigs.length === 0 ? {
                        ...s, roomConfigs: [
                            { id: Date.now(), room: '', roomType: types[0].name, roomRate: String(types[0].basePrice || types[0].base_price || ''), adults: '2', children: '0', roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(types[0].basePrice || types[0].base_price || '') }
                        ]
                    } : s);
                }
            });
        }
    }, [hotel]);


    const handleRoomConfigChange = (id, changes) => {
        setStay(prev => {
            const nextConfigs = prev.roomConfigs.map(c => {
                if (c.id !== id) return c;
                let next = { ...c, ...changes };
                if ('roomType' in changes || 'adults' in changes || 'children' in changes || 'capacityPreference' in changes || 'roomsCount' in changes || 'offeredPrice' in changes) {
                    const typeDef = roomTypes.find(t => t.name === next.roomType);
                    if (typeDef) {
                        const adults = Number(next.adults) || 1;
                        const children = Number(next.children) || 0;
                        const baseA = typeDef.baseAdults || typeDef.base_adults || 2;
                        const baseC = typeDef.baseChildren || typeDef.base_children || 2;

                        // Reset offered price when room type changes
                        if ('roomType' in changes) {
                            next.offeredPrice = String(typeDef.basePrice || typeDef.base_price || 0);
                        }

                        const rc = Math.max(1, Number(next.roomsCount) || 1);
                        let eb = 0;
                        if (next.capacityPreference !== 'exception') {
                            eb = Math.max(0, adults - (rc * baseA));
                            if (children > (rc * baseC)) eb += (children - (rc * baseC));
                        }

                        next.roomsCount = rc;
                        next.extraBeds = eb;

                        const basePrice = (typeDef.basePrice || typeDef.base_price || 0);
                        const ebPrice = (typeDef.extraBedPrice || typeDef.extra_bed_price || 0);
                        const offeredPrice = Number(next.offeredPrice) || basePrice;

                        // roomRate = total nightly cost for ALL rooms in this config
                        next.roomRate = String(Math.round((rc * offeredPrice + eb * ebPrice) * 100) / 100);
                    }
                }
                return next;
            });
            return { ...prev, roomConfigs: nextConfigs };
        });
    };

    const removeRoomConfig = (id) => {
        setStay(prev => ({ ...prev, roomConfigs: prev.roomConfigs.filter(c => c.id !== id) }));
    };

    const totalRoomRate = stay.roomConfigs.reduce((sum, c) => {
        // Each config's roomRate already represents the full nightly cost for that config.
        return sum + (Number(c.roomRate) || 0);
    }, 0);
    const mkPayRow = () => ({ id: Date.now() + Math.random(), type: 'cash', last5: '', amount: '', note: '' });
    const [payments, setPayments] = useState([mkPayRow()]);

    // Keep the single-row (common) case effortless: as long as staff hasn't
    // split into multiple payment methods, the one row always mirrors the
    // advance amount above it so nothing extra needs to be typed twice.
    useEffect(() => {
        setPayments(prev => prev.length === 1 ? [{ ...prev[0], amount: stay.advancePaid }] : prev);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stay.advancePaid]);

    const [phoneConflict, setPhoneConflict] = useState(null);

    const searchGuest = async (p = phoneSearch) => {
        const term = String(p).replace(/\D/g, '');
        if (!term) return;
        if (term.length !== 10) {
            onSuccess?.('Please enter a valid 10-digit mobile number.', 'error');
            return;
        }
        try {
            const guestList = await guestsApi.list(term);
            const g = guestList[0];
            if (g) {
                setExistingGuest(g);
                const rawIdType = g.id_type || g.idType || 'Aadhaar Card';
                const rawIdNum = g.id_number || g.idNumber || '';
                setGuest({
                    ...guest,
                    id: g.id,
                    name: g.name, phone: g.phone, email: g.email || '',
                    nationality: g.nationality || 'Indian',
                    customerType: g.customer_type || g.customerType || 'individual',
                    idType: rawIdType,
                    idNumber: rawIdType === 'Aadhaar Card' ? formatAadhaar(rawIdNum) : rawIdNum,
                    address: g.address || '', 
                    company: g.company || '', companyGst: g.company_gst || g.companyGst || '',
                });
                setPhoneConflict(null);
            } else {
                setExistingGuest(null);
                setPhoneConflict(null);
            }
        } catch (err) { console.error(err); }
    };

    const checkPhoneConflict = async () => {
        if (!guest.phone || guest.phone.length < 5) return null;
        const cleanInput = guest.phone.replace(/[\s.\-()+]/g, '');
        const gs = await guestsApi.list(cleanInput);
        
        const conflict = gs.find(g => {
          if (existingGuest && g.id === existingGuest.id) return false;
          const cleanExisting = g.phone.replace(/[\s.\-()+]/g, '');
          return cleanExisting === cleanInput && g.name.trim().toLowerCase() !== guest.name.trim().toLowerCase();
        });
        
        if (conflict) {
          setPhoneConflict(conflict.name);
          return conflict.name;
        }
        setPhoneConflict(null);
        return null;
    };

    const calcNights = (cin, cout) => {
        if (!cin || !cout) return 0;
        const d = (new Date(cout) - new Date(cin)) / 86400000;
        return Math.max(1, Math.round(d));
    };

    const bill = calcBill({ roomRate: totalRoomRate, nights: Number(stay.nights) || 1, extraCharges: Number(stay.extraCharges), advancePaid: 0 }, stay.billingType, hotel?.gstRate);

    const handleProofUpload = async (e) => {
        const files = Array.from(e.target.files);
        for (const file of files) {
            try {
                await validateImageFile(file);
                const reader = new FileReader();
                reader.onload = (ev) => {
                    setProofFiles(prev => [...prev, { name: file.name, data: ev.target.result, type: file.type }]);
                };
                reader.readAsDataURL(file);
            } catch (err) {
                await notify(err.message);
            }
        }
    };

    const handleSubmit = async () => {
        // Was built (the warning banner already exists below) but never actually
        // wired to run — meaning a phone number already registered to a different
        // guest was silently allowed through pre-booking with no warning at all,
        // unlike the equivalent check-in flow which blocks on this.
        const conflict = await checkPhoneConflict();
        if (conflict) {
            onSuccess?.(`Phone already registered to "${conflict}".`, 'error');
            return;
        }
        const advanceTotal = Number(stay.advancePaid) || 0;
        const activePayments = payments.filter(p => Number(p.amount) > 0);
        if (advanceTotal > 0) {
            const splitTotal = activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
            if (Math.abs(advanceTotal - splitTotal) > 0.5) {
                onSuccess?.(`Payment split (${fmt(splitTotal)}) doesn't match the advance amount (${fmt(advanceTotal)}).`, 'error');
                return;
            }
            const missingRef = activePayments.find(p => ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) && !p.last5);
            if (missingRef) {
                onSuccess?.('Enter the reference (last 5 digits) for every non-cash payment.', 'error');
                return;
            }
        }
        setLoading(true);
        try {
            const [allRooms, existingRes] = await Promise.all([
                roomsApi.list(hotel.id),
                reservationsApi.list(hotel.id)
            ]);
            
            // 1. Map total inventory by type from the fetched roomTypes state
            const inventoryByType = {};
            roomTypes.forEach(rt => {
                const t = rt.name || 'Unknown';
                inventoryByType[t] = Number(rt.total_rooms || rt.totalRooms || 0);
            });

            // 2. Group new requested rooms by type
            const requestedByType = {};
            stay.roomConfigs.forEach(c => {
                const t = c.roomType;
                requestedByType[t] = (requestedByType[t] || 0) + (Number(c.roomsCount) || 1);
            });

            const start = new Date(stay.checkIn);
            const effectiveCheckOut = getEffectiveCheckoutDate(stay.checkIn, stay.checkOut);
            const endOut = effectiveCheckOut ? new Date(effectiveCheckOut) : new Date(start.getTime() + 86400000);

            // 3. For each night, verify availability for every requested type.
            // `start`/`endOut` are parsed from "YYYY-MM-DD" strings, which JS treats as
            // UTC midnight — advancing with the LOCAL setDate() mixed UTC-parsed input
            // with local-time increments, which in IST (UTC+5:30) could roll the computed
            // date backwards by a day once converted back via toISOString(). Advancing
            // with setUTCDate keeps the whole loop in UTC space consistently.
            for (let d = new Date(start); d < endOut; d.setUTCDate(d.getUTCDate() + 1)) {
                const currentDateStr = d.toISOString().split('T')[0];
                const bookedOnDayByType = {};

                for (const r of existingRes) {
                    if (r.status !== 'confirmed' && r.status !== 'checked-in') continue;
                    
                    const rStart = new Date(r.check_in);
                    const rEnd = new Date(r.check_out);

                    if (d >= rStart && d < rEnd) {
                        if (r.room && r.room.trim()) {
                            // Count by actual assigned room types
                            const assignedRooms = r.room.split(',').map(s => s.trim()).filter(Boolean);
                            assignedRooms.forEach(num => {
                                const rmObj = allRooms.find(rm => rm.number === num);
                                if (rmObj) {
                                    const t = rmObj.room_type || 'Unknown';
                                    bookedOnDayByType[t] = (bookedOnDayByType[t] || 0) + 1;
                                }
                            });
                        } else {
                            // Parse room_type string (e.g., "Deluxe x2 + Standard")
                            const parts = (r.room_type || '').split('+');
                            parts.forEach(p => {
                                const cleanPart = p.trim();
                                const match = cleanPart.match(/(.+) x(\d+)/) || [null, cleanPart, 1];
                                const typeName = (match[1] || cleanPart).trim();
                                const count = parseInt(match[2]) || 1;
                                bookedOnDayByType[typeName] = (bookedOnDayByType[typeName] || 0) + count;
                            });
                        }
                    }
                }

                // Final check: Requested + Booked > Inventory for any type?
                for (const [type, reqCount] of Object.entries(requestedByType)) {
                    const booked = bookedOnDayByType[type] || 0;
                    const inventory = inventoryByType[type] || 0;
                    if (booked + reqCount > inventory) {
                        throw new Error(`Overbooking Alert on ${currentDateStr}: No ${type} rooms available. (Booked: ${booked}, Requested: ${reqCount}, Total: ${inventory})`);
                    }
                }
            }

            let guestId = existingGuest?.id;
            const guestData = {
                name: guest.name, phone: guest.phone, email: guest.email,
                nationality: guest.nationality,
                customerType: guest.customerType, customer_type: guest.customerType,
                idType: guest.idType, id_type: guest.idType,
                idNumber: guest.idNumber, id_number: guest.idNumber,
                address: guest.address, company: guest.company,
                companyGst: guest.companyGst, company_gst: guest.companyGst
            };
            
            if (!guestId) {
                const newG = await guestsApi.create(guestData);
                guestId = newG.id;
            } else {
                await guestsApi.update(guestId, guestData);
            }

            const combinedRoomType = stay.roomConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ');
            const resData = {
                guestId, hotelId: hotel.id,
                roomType: combinedRoomType,
                checkIn: stay.checkIn, checkOut: stay.checkOut,
                checkInTime: stay.checkInTime, status: 'confirmed', 
                nights: calcNights(stay.checkIn, stay.checkOut) || 1,
                billingCycle: stay.billingCycle,
                roomRate: totalRoomRate, extraCharges: Number(stay.extraCharges) || 0,
                totalAmount: bill.total, advancePaid: advanceTotal,
                advancePayments: activePayments.map(p => ({ type: p.type, amount: Number(p.amount) || 0, last5: p.last5 || null, note: p.note || null })),
                customerType: guest.customerType, 
                company: guest.company, 
                companyGst: guest.companyGst,
                billingType: stay.billingType,
                notes: stay.notes,
                roomTypeConfigs: stay.roomConfigs.map(c => {
                    const typeDef = roomTypes.find(t => t.name === c.roomType);
                    const basePrice = Number(typeDef?.basePrice ?? typeDef?.base_price ?? c.basePrice ?? c.offeredPrice ?? c.roomRate ?? 0);
                    const offeredPrice = Number(c.offeredPrice ?? c.roomRate ?? 0);
                    return {
                        roomType: c.roomType,
                        roomsCount: c.roomsCount,
                        roomRate: Number(c.roomRate) || 0,
                        basePrice,
                        offeredPrice
                    };
                })
            };

            const newRes = await reservationsApi.create(resData);
            const resId = newRes.id;

            const proofUploadErrors = [];
            for (const proof of proofFiles) {
                try {
                    await proofsApi.create({
                        guestId,
                        hotelId: hotel.id,
                        reservationId: resId,
                        type: guest.idType,
                        fileName: proof.name,
                        data: proof.data
                    });
                } catch (err) {
                    proofUploadErrors.push(`${proof.name}: ${err.message}`);
                }
            }

            onSuccess?.(
                proofUploadErrors.length > 0
                    ? `Reservation pre-booked, but ${proofUploadErrors.length} proof upload(s) failed. ${proofUploadErrors[0]}`
                    : 'Reservation pre-booked successfully!',
                proofUploadErrors.length > 0 ? 'error' : 'success'
            );
            setStep(0); setExistingGuest(null); setPhoneSearch(''); setProofFiles([]);
            setGuest({ name: '', phone: '', email: '', nationality: 'Indian', customerType: 'individual', idType: 'Aadhaar Card', idNumber: '', address: '', company: '', companyGst: '' });
            setStay(s => ({ ...s, checkIn: '', checkOut: '', advancePaid: '0', notes: '', roomConfigs: [{ id: Date.now(), room: '', roomType: roomTypes[0]?.name || '', roomRate: String(roomTypes[0]?.basePrice || roomTypes[0]?.base_price || ''), adults: '2', children: '0', roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(roomTypes[0]?.basePrice || roomTypes[0]?.base_price || '') }] }));
            setPayments([mkPayRow()]);
        } catch (err) {
            onSuccess?.('Error: ' + err.message, 'error');
        }
        setLoading(false);
    };

    return (
        <div>
            <div className="panel">
                <div className="panel-head"><span className="panel-title">Create Pre-booking</span></div>
                <div style={{ padding: '20px 24px' }}>
                    
                    {/* ─── SECTION 1: GUEST DETAILS ─── */}
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 14 }}>👤 Guest Details</div>
                    <div style={{ background: 'var(--s2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 10 }}>🔍 Search Existing Guest</div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <input className="field input" style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font-b)', fontSize: 13, outline: 'none' }} placeholder="Enter 10-digit phone..." value={phoneSearch} maxLength={10} onChange={e => setPhoneSearch(e.target.value.replace(/\D/g, '').slice(0, 10))} onKeyDown={e => e.key === 'Enter' && searchGuest()} />
                            <button className="btn btn-teal" onClick={() => searchGuest()}>Search</button>
                        </div>
                        {existingGuest && <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(109,191,148,0.1)', border: '1px solid rgba(109,191,148,0.2)', borderRadius: 8, fontSize: 12 }}>✅ Existing guest found: <strong>{existingGuest.name}</strong></div>}
                    </div>
                    
                    <div className="form-row-2">
                        <div className="field"><label>Full Name *</label><input value={guest.name} onChange={e => setGuest(f => ({ ...f, name: e.target.value }))} /></div>
                        <div className="field"><label>Phone *</label><input value={guest.phone} onChange={e => setGuest(f => ({ ...f, phone: formatPhone(e.target.value) }))} /></div>
                    </div>
                    <div className="form-row-2" style={{ marginTop: 14 }}>
                        <div className="field"><label>Email</label><input value={guest.email} onChange={e => setGuest(f => ({ ...f, email: e.target.value }))} /></div>
                        <div className="field"><label>Nationality</label><select value={guest.nationality} onChange={e => {
                            const val = e.target.value;
                            setGuest(f => ({ ...f, nationality: val, idType: val !== 'Indian' ? 'Passport' : f.idType }));
                        }}>{NATIONALITIES.map(n => <option key={n}>{n}</option>)}</select></div>
                    </div>
                    <div className="field" style={{ marginTop: 14 }}>
                        <label>Customer Type *</label><select value={guest.customerType} onChange={e => setGuest(f => ({ ...f, customerType: e.target.value }))}><option value="individual">Individual</option><option value="corporate">Corporate</option></select>
                    </div>
                    {guest.customerType === 'corporate' && (
                        <div className="form-row-2" style={{ marginTop: 14 }}>
                            <div className="field"><label>Company Name *</label><input value={guest.company} onChange={e => setGuest(f => ({ ...f, company: e.target.value }))} /></div>
                            <div className="field"><label>Company GST *</label><input value={guest.companyGst} onChange={e => setGuest(f => ({ ...f, companyGst: e.target.value.toUpperCase() }))} /></div>
                        </div>
                    )}
                    <div style={{ marginTop: 14 }}><div className="field"><label>Address</label><textarea value={guest.address} onChange={e => setGuest(f => ({ ...f, address: e.target.value }))} /></div></div>
                    <div className="form-row-2" style={{ marginTop: 14 }}>
                        <div className="field"><label>ID Type *</label><select value={guest.idType} onChange={e => setGuest(f => ({ ...f, idType: e.target.value }))}>{ID_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
                        <div className="field"><label>ID Number *</label>
                            <input 
                                value={guest.idNumber} 
                                onChange={e => {
                                    const val = e.target.value;
                                    let formatted = val;
                                    if (guest.idType === 'Aadhaar Card') formatted = formatAadhaar(val);
                                    else if (guest.idType === 'PAN Card') formatted = formatPAN(val);
                                    setGuest(f => ({ ...f, idNumber: formatted }));
                                }} 
                                maxLength={guest.idType === 'Aadhaar Card' ? 14 : undefined}
                            />
                        </div>
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' }} />

                    {/* ─── SECTION 2: STAY DETAILS ─── */}
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 14 }}>📅 Stay & Billing Details</div>
                    <div className="form-row-2">
                        <div className="field"><label>Check-in Date *</label><input type="date" min={getTodayStr()} value={stay.checkIn} onChange={e => setStay(f => ({ ...f, checkIn: e.target.value, checkOut: (!f.checkOut || e.target.value > f.checkOut) ? '' : f.checkOut, nights: calcNights(e.target.value, f.checkOut) || 1 }))} /></div>
                        <div className="field"><label>Check-out Date *</label><input type="date" min={stay.checkIn || getTodayStr()} value={stay.checkOut} onChange={e => setStay(f => ({ ...f, checkOut: e.target.value, nights: calcNights(f.checkIn, e.target.value) || 1 }))} /></div>
                    </div>
                    <div className="form-row-3" style={{ marginTop: 14 }}>
                        <div className="field"><label>Nights / Slots</label><input type="number" min="1" value={stay.nights} onChange={e => setStay(f => ({ ...f, nights: e.target.value }))} /></div>
                        <div className="field"><label>Billing Cycle</label><select value={stay.billingCycle} onChange={e => setStay(f => ({ ...f, billingCycle: e.target.value }))}><option value="24h">24 Hours</option><option value="12h">12 Hours</option></select></div>
                    </div>

                    <div style={{ marginTop: 24, marginBottom: 10, fontSize: 12, fontWeight: 700, opacity: 0.7 }}>Room Selection</div>
                    {stay.roomConfigs.map((config, index) => {
                        const typeDef = roomTypes.find(t => t.name === config.roomType);
                        const totalRooms = typeDef ? (Number(typeDef.totalRooms || typeDef.total_rooms) || 1) : 1;
                        const availCount = availability[config.roomType] !== undefined ? availability[config.roomType] : totalRooms;
                        const basePrice = typeDef ? (typeDef.basePrice || typeDef.base_price || 0) : 0;
                        const ebPrice = typeDef ? (typeDef.extraBedPrice || typeDef.extra_bed_price || 0) : 0;
                        const offeredPrice = config.offeredPrice !== undefined ? Number(config.offeredPrice) : basePrice;
                        const extraBedCost = (config.extraBeds || 0) > 0 ? (ebPrice * ((config.extraBeds || 0) / (config.roomsCount || 1))) : 0;
                        const finalCostNight = offeredPrice + extraBedCost;

                        return (
                        <div key={config.id} style={{ background: 'var(--s1)', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>Stay Configuration {index+1}</div>
                                {stay.roomConfigs.length > 1 && <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => removeRoomConfig(config.id)}>Remove</button>}
                            </div>
                            <div className="form-row-2">
                                <div className="field"><label>Adults</label><input type="number" value={config.adults} onChange={e => handleRoomConfigChange(config.id, { adults: e.target.value })} /></div>
                                <div className="field"><label>Children</label><input type="number" value={config.children} onChange={e => handleRoomConfigChange(config.id, { children: e.target.value })} /></div>
                            </div>
                            <div className="form-row-2" style={{ marginTop: 14 }}>
                                <div className="field">
                                    <label>Room Type</label>
                                    <select value={config.roomType} onChange={e => handleRoomConfigChange(config.id, { roomType: e.target.value })}>
                                        {roomTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="field">
                                    <label>No. Of Rooms {(!stay.checkIn || !stay.checkOut) && <span style={{ color: 'var(--danger)', fontStyle: 'italic', fontWeight: 400 }}>(Select dates first)</span>}</label>
                                    <select 
                                        disabled={!stay.checkIn || !stay.checkOut}
                                        value={config.roomsCount} 
                                        onChange={e => handleRoomConfigChange(config.id, { roomsCount: e.target.value })}
                                    >
                                        {availCount > 0 ? (
                                            Array.from({ length: availCount }, (_, i) => i + 1).map(num => (
                                                <option key={num} value={num}>{num} {num === 1 ? 'Room' : 'Rooms'}</option>
                                            ))
                                        ) : (
                                            <option value="0" disabled>{stay.checkIn && stay.checkOut ? 'Rooms already prebooked' : 'Select dates'}</option>
                                        )}
                                    </select>
                                    {availCount > 0 && <span style={{ fontSize: 10, color: 'var(--success)', marginTop: 4 }}>Available for this period: {availCount}</span>}
                                </div>
                            </div>
                            <div className="form-row-3" style={{ marginTop: 14 }}>
                                <div className="field"><label>Standard Base Rate / Room (₹)</label><input type="number" disabled value={basePrice} /></div>
                                <div className="field"><label>Offered Price / Room (₹)</label><input type="number" value={config.offeredPrice !== undefined ? config.offeredPrice : basePrice} onChange={e => handleRoomConfigChange(config.id, { offeredPrice: e.target.value })} style={{ fontWeight: 700, color: 'var(--teal)', border: '1px solid var(--teal)', outline: 'none' }} /></div>
                                <div className="field"><label>Final Cost / Room (with extra bed) (₹)</label><input type="number" disabled value={finalCostNight} style={{ fontWeight: 700, color: 'var(--success)' }} /></div>
                            </div>
                            <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, textAlign: 'right', color: 'var(--muted)' }}>
                                Subtotal for this group: {fmt(Number(config.roomRate) * stay.nights)}
                            </div>
                        </div>
                    )})}
                    <div style={{ marginTop: 14 }}><div className="field"><label>Reservation Note</label><textarea value={stay.notes} onChange={e => setStay(f => ({ ...f, notes: e.target.value }))} /></div></div>
                    <div className="field" style={{ marginTop: 14 }}>
                        <label>Billing Configuration *</label>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                            {BILLING_TYPES.map(bt => (
                                <label key={bt.value} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, border: `2px solid ${stay.billingType === bt.value ? 'var(--accent)' : 'var(--border)'}`, background: stay.billingType === bt.value ? 'var(--s1)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <input type="radio" checked={stay.billingType === bt.value} onChange={() => setStay(s => ({ ...s, billingType: bt.value }))} style={{ width: 16, height: 16 }} />
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 700 }}>{bt.label}</div>
                                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>Series: {bt.series} · {bt.value === 'gst' ? `${hotel?.gstRate || 5}% Tax` : '0% Tax'}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' }} />

                    {/* ─── SECTION 3: ID PROOF ─── */}
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 14 }}>📎 ID Proof Upload (Scanned Copy)</div>
                    <div className="proof-zone" onClick={() => fileRef.current?.click()} style={{ cursor: 'pointer' }}>
                        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple onChange={handleProofUpload} hidden />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
                            <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>Click to upload {guest.idType}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Accepts JPG, PNG, PDF (Multiple allowed)</div>
                        </div>
                    </div>
                    {proofFiles.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                               🛡️ SCAN COMPLETE — Verified Secure
                            </div>
                            <div className="proof-preview">
                                {proofFiles.map((f, i) => (
                                    <div key={i} className="proof-thumb" style={{ position: 'relative' }}>
                                        {f.type.startsWith('image/') 
                                            ? <img src={f.data} alt="ID Proof" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} /> 
                                            : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: 6, fontSize: 24 }}>📄</div>
                                        }
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 9, padding: '2px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                                        <button className="proof-remove" onClick={() => setProofFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: -8, right: -8, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' }} />

                    {/* ─── SECTION 4: PAYMENT ─── */}
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 14 }}>💰 Advance Configuration</div>
                    <div className="field" style={{ marginBottom: 20 }}><label>Advance Payment Received (₹)</label><input type="number" value={stay.advancePaid} onChange={e => setStay(f => ({ ...f, advancePaid: e.target.value }))} /></div>
                    {Number(stay.advancePaid) > 0 && payments.map((p, idx) => {
                        const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type);
                        const advanceTotal = Number(stay.advancePaid) || 0;
                        const otherTotal = payments.filter(r => r.id !== p.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                        const remaining = Math.max(0, advanceTotal - otherTotal);
                        return (
                            <div key={p.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>💳 Payment {idx + 1}</span>
                                    {payments.length > 1 && (
                                        <button type="button" onClick={() => setPayments(prev => prev.filter(r => r.id !== p.id))}
                                            style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
                                    )}
                                </div>
                                <div className="pay-type-grid">{PAYMENT_TYPES.map(pt => <button key={pt.value} className={`pay-type-btn ${p.type === pt.value ? 'selected' : ''}`} onClick={() => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, type: pt.value, last5: '' } : r))}>{pt.label}</button>)}</div>
                                <div className="form-row-2" style={{ marginTop: 12 }}>
                                    <div className="field">
                                        <label>Amount (₹) <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>Remaining: {fmt(remaining)}</span></label>
                                        <input type="number" value={p.amount} onChange={e => {
                                            const val = Number(e.target.value) || 0;
                                            if (val > remaining + 0.01) return;
                                            setPayments(prev => prev.map(r => r.id === p.id ? { ...r, amount: e.target.value } : r));
                                        }} />
                                    </div>
                                    {isNonCash && (
                                        <div className="field">
                                            <label>
                                                {p.type === 'cheque' ? 'Cheque Number (Last 5 Digits)' :
                                                 p.type === 'bank_transfer' ? 'Transaction ID (Last 5 Digits)' :
                                                 'Transaction Ref (Last 5 Digits)'}
                                            </label>
                                            <input value={p.last5} onChange={e => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, last5: e.target.value.slice(-5) } : r))} placeholder="Reference #" maxLength={5} />
                                        </div>
                                    )}
                                </div>
                                <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                                    <label>Remarks <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
                                    <input value={p.note} onChange={e => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, note: e.target.value } : r))} placeholder="Reference / note" />
                                </div>
                            </div>
                        );
                    })}
                    {(() => {
                        const advanceTotal = Number(stay.advancePaid) || 0;
                        if (advanceTotal <= 0) return null;
                        const splitTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                        const diff = advanceTotal - splitTotal;
                        return (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: Math.abs(diff) < 0.5 ? 'rgba(109,191,148,0.12)' : 'rgba(232,184,109,0.10)' }}>
                                <span style={{ fontSize: 12, fontWeight: 600 }}>{Math.abs(diff) < 0.5 ? '✅ Fully allocated' : `⚠️ ${fmt(diff)} not yet allocated`}</span>
                                {diff > 0.5 && (
                                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPayments(prev => [...prev, { ...mkPayRow(), amount: String(diff) }])}>+ Add another payment method</button>
                                )}
                            </div>
                        );
                    })()}
                    <div style={{ marginTop: 20 }} className="bill-box">
                        <div className="bill-row"><span>Total Booking Amount</span><span>{fmt(bill.total)}</span></div>
                        <div className="bill-row"><span>Advance Paid</span><span style={{ color: 'var(--success)' }}>- {fmt(Number(stay.advancePaid) || 0)}</span></div>
                        <div className="bill-row bill-due"><span>Pending at Arrival</span><span>{fmt(bill.total - (Number(stay.advancePaid) || 0))}</span></div>
                    </div>

                    {phoneConflict && (
                        <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8, padding: '12px 16px', color: '#c53030', fontSize: 13, margin: '16px 0' }}>
                            ⚠️ <strong>Conflict:</strong> This mobile belongs to <strong>"{phoneConflict}"</strong>. Use that name or a different phone.
                        </div>
                    )}

                    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-gold btn-lg" onClick={handleSubmit} disabled={loading || !guest.name || !guest.phone || !guest.idNumber || stay.roomConfigs.some(c => !c.roomsCount || Number(c.roomsCount) <= 0)}>
                            {loading ? 'Booking...' : '✅ Confirm Pre-booking'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
